import React from 'react'
import clsx from 'clsx'

import Search from '../Search'
import PeerChatActions from './PeerChatActions'
import AvatarSection from './AvatarSection'
import NotificationBackButton from '../../../molecules/NotificationBackButton'
import FavoriteButton from '@SonarCX/components/atoms/FavoriteButton'
import PinButton from '@SonarCX/components/atoms/PinButton'

import { useAgent } from '@SonarCX/providers/AgentProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useVerification } from '@SonarCX/providers/VerificationProvider'
import useFavorites from '@SonarCX/hooks/useFavorites'

import useStyles from './styles'

const Header = ({
  isDirect,
  isClosed,
  users,
  convo,
  setShowQueue,
  onMobile,
  showBack,
  viewing,
  session
}) => {
  const { currentUser } = useAgent()
  const { allUsers } = useUserAPI()
  const { deleted: ownerDeleted } = useOwner()
  const { handleSelect: handleConvoSelect } = useQueueState()
  const { down990 } = useBreakpoint()
  const { verified } = useVerification() ?? { verified: true }
  const { favoritePeerChats } = useFavorites()
  const isPersonalSpace = React.useMemo(
    () =>
      isDirect &&
      !convo?.isGroup &&
      convo?.users?.every(i => currentUser()?.id === i),
    [currentUser, isDirect, convo?.users, convo?.isGroup]
  )
  const showActions = React.useMemo(() => !isClosed, [isClosed])
  const userIDs = React.useMemo(
    () => (users ? users.filter(id => id !== currentUser()?.id) : []),
    [users, currentUser]
  )
  const isFavorite = React.useMemo(() => {
    if (!convo?.session) return false

    return favoritePeerChats.some(p => p.item_id === convo.session)
  }, [favoritePeerChats, convo])

  const avatarProps = { userIDs, intersect: true, isDirect }
  const centerContent = down990

  const classes = useStyles({ isDirect, verified, centerContent })

  // Comma separated list of user names
  const allNames = React.useMemo(() => {
    if (!isDirect) {
      return ''
    }

    if (convo.name) {
      return convo.name
    }

    const names = []
    userIDs.forEach(id => {
      const user = allUsers[id]
      if (user) {
        names.push(user.name)
      }
    })
    return names.join(', ')
  }, [isDirect, allUsers, userIDs, convo])

  const handleBackClick = React.useCallback(() => {
    setShowQueue(true)
    handleConvoSelect(null)
  }, [setShowQueue, handleConvoSelect])

  if (ownerDeleted) {
    return (
      <div className={classes.root}>
        <div className={classes.empty} />
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <NotificationBackButton
        show={showBack}
        onClick={handleBackClick}
        hideCount={viewing}
      />

      <div className={clsx(classes.section, classes.nameSection)}>
        <AvatarSection
          avatarProps={avatarProps}
          names={allNames}
          nameClass={classes.name}
          convo={convo}
          isPersonalSpace={isPersonalSpace}
        />
      </div>

      <div className={classes.section}>
        {!onMobile && <Search session={session} isDirect={isDirect} />}

        {showActions && (
          <>
            <PeerChatActions disable={Boolean(allUsers[convo?.session])} />

            <FavoriteButton
              isFavorite={isFavorite}
              type='peer_chat'
              itemId={convo?.session}
            />

            <PinButton
              id={convo?.session}
              type='team-chat'
              title={allNames}
              extra={{ isGroup: convo?.isGroup }}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default Header
